import React, { useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { isTerminalJob, JobSummary } from "../../../../../database/mediaOps/mediaOpsApi";
import { useMutation, useOpRead } from "./hooks";
import { jobChip } from "./stateLabels";
import { ErrorLine, FieldLabel, FormGrid, Grow, Mono, Panel, PanelHead, ResultNotice, Select, Small, Table, TableScroll, fmtTime, usd } from "./ui";

// Jobs: render, generation, import, scans. Cost is shown as reserved and
// actual. Cancelling a running job may not recall work a provider started.

const STATUSES = ["", "queued", "running", "succeeded", "failed", "cancelled"];

const pct = (p: number) => `${Math.round((p || 0) * (p <= 1 ? 100 : 1))}%`;

export const JobsView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const [status, setStatus] = useState("");
  const read = useOpRead(() => mediaOps.listJobs(communityId, status ? { status } : {}), [communityId, status]);
  const jobs: JobSummary[] = read.result?.data?.jobs || [];
  const cancel = useMutation();
  const [cancelling, setCancelling] = useState<string | null>(null);

  const doCancel = async (j: JobSummary) => {
    setCancelling(j.jobId);
    const res = await cancel.run((k) => mediaOps.cancelJob(k, communityId, { jobId: j.jobId, expectedVersion: j.version }));
    setCancelling(null);
    if (res && !res.transportError) read.reload();
  };

  return (
    <Panel>
      <PanelHead>
        Jobs <Grow />
        <Button size="sm" variant="secondary" onClick={read.reload}>
          Refresh
        </Button>
      </PanelHead>
      <FormGrid>
        <FieldLabel>
          Status
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s || "Any status"}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>
      {read.result?.status === "blocked" ? <ResultNotice result={read.result} /> : null}
      <ResultNotice result={cancel.result} okText="Cancel recorded." />
      {jobs.length === 0 && read.result?.status !== "blocked" ? <Small>No jobs.</Small> : null}
      {jobs.length > 0 ? (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>Job</th>
                <th>Type</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Reserved</th>
                <th>Actual</th>
                <th>Unknown cost</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const chip = jobChip(j.status);
                return (
                  <tr key={j.jobId}>
                    <td>
                      <Mono>{j.jobId}</Mono>
                      {j.error ? <ErrorLine error={j.error} /> : null}
                    </td>
                    <td>{j.type}</td>
                    <td>
                      <Tag size="sm" variant={chip.variant}>
                        {chip.label}
                      </Tag>
                      {j.cancelRequested ? <Small> Cancel requested</Small> : null}
                    </td>
                    <td>
                      {j.stage || "none"} {pct(j.progress)}
                    </td>
                    <td>{usd(j.budget?.reservedUsd)}</td>
                    <td>{usd(j.budget?.actualUsd)}</td>
                    <td>{usd(j.budget?.unsettledUsd)}</td>
                    <td>{fmtTime(j.createdAt)}</td>
                    <td>
                      {!isTerminalJob(j.status) ? (
                        <Button size="sm" variant="ghost" onClick={() => doCancel(j)} isLoading={cancelling === j.jobId}>
                          Cancel
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableScroll>
      ) : null}
    </Panel>
  );
};

export default JobsView;
